import React from 'react';

/**
 * Lightweight error boundary. Catches JS errors in child route trees so a
 * single failing component doesn't blank the entire engineer PWA.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      const fallback = this.props.fallback;
      if (fallback) {
        return typeof fallback === 'function'
          ? fallback({ error: this.state.error, reset: this.reset })
          : fallback;
      }
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
        >
          <div className="max-w-md w-full bg-white rounded-xl border border-rose-200 shadow-sm p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-3 text-2xl">!</div>
            <h2 className="text-lg font-bold text-slate-900 mb-1">Something went wrong</h2>
            <p className="text-sm text-slate-500 mb-4">
              The app hit a snag. Try the actions below or refresh the page.
            </p>
            <pre className="text-[10px] text-left bg-slate-50 border border-slate-200 rounded p-2 mb-3 overflow-auto max-h-24 text-slate-600">
              {String(this.state.error?.message || this.state.error || 'Unknown error')}
            </pre>
            <div className="flex gap-2 justify-center">
              <button
                onClick={this.reset}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                data-testid="error-boundary-retry"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-300"
                data-testid="error-boundary-reload"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
